import { useRef, useEffect, useLayoutEffect, useState } from 'react';
import {
  Box,
  Card,
  CardMedia,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Button, Dialog
} from '@mui/material';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { ChevronLeft, ChevronRight, ShoppingCart } from "@mui/icons-material";
import CardDetail from './cardDetail';
import noImage from '../../images/noimage.png';
const API_URI = process.env.REACT_APP_API_URI;

const ShopingCard = ({product}) => {
  const [record, setRecord] = useState([]);
  const [imgLoaded, setImgLoaded] = useState(false);
  const scrollRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);
  const [currentImg, setCurrentImg] = useState(noImage);

  useEffect(() => {
    const imgs = product.Images?product.Images.split(';')[0]:'';
    const imgSrc = product.ImageSrcs?product.ImageSrcs.split(';')[0]:'';
    const arrColor = product.Colors ? product.Colors.split(';') : null;
    const Color = arrColor ? arrColor[0] : '';
    setRecord({...product, imgSrc, imgs, arrColor, Color});
    if(imgs)
      setCurrentImg(`${API_URI}/uploads/products/${imgs}`);
    else if(imgSrc)
      setCurrentImg(imgSrc);
  }, [product]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);
    return () => {
      if (el) el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, []);

  useLayoutEffect(() => {// Run after layout
    checkScroll();
  }, [record]);

  const handleChangeColor=(color, colorIndex)=>{
    const selectedRecord = {...record};
    selectedRecord.Color = color;
    selectedRecord.ColorIndex = colorIndex;
    selectedRecord.imgs = product.Images?product.Images.split(';')[colorIndex]:'';
    selectedRecord.imgSrc = product.ImageSrcs?product.ImageSrcs.split(';')[colorIndex]:'';
    
    setRecord(selectedRecord);
    if(selectedRecord.imgs){
      setCurrentImg(`${API_URI}/uploads/products/${selectedRecord.imgs}`);
      setImgLoaded(false);
    }
    else if(selectedRecord.imgSrc){
      setCurrentImg(selectedRecord.imgSrc);
      setImgLoaded(false);
    }
    else
      setCurrentImg(noImage);
  }

  const handleImageLoad = () => {
    setImgLoaded(true);
  };

  const handleImageError = () => {
    setImgLoaded(true);
    setCurrentImg(noImage);
  };

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 0);
    setShowRight(el.scrollWidth > el.clientWidth + el.scrollLeft);
  };

  const scrollByAmount = (amount) => {
    scrollRef.current.scrollBy({ left: amount, behavior: "smooth" });
  };

  return (
    <Card sx={{ position: 'relative', fontFamily: 'sans-serif' }}>
      {/* Wishlist Icon */}
      <IconButton
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 2,
          backgroundColor: 'white',
          '&:hover': {
            backgroundColor: '#f0f0f0',
          },
        }}
      >
        <FavoriteBorderIcon fontSize="small" />
      </IconButton>

      {/* Product Image */}
      <CardMedia
        component="img"
        height="280"
        image={currentImg}
        alt="Product Image"
        onLoad={handleImageLoad}
        onError={handleImageError}
        style={{
          filter: imgLoaded ? 'none' : 'blur(5px)',
          transition: 'filter 0.3s ease-in-out',
          cursor: 'pointer'
        }}
        onClick={()=>setOpenDetail(true)}
      />

      {/* Content */}
      <CardContent sx={{ p: 2 }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 500,
            display: "-webkit-box",
            overflow: "hidden",
            WebkitLineClamp: 2, // limits to 2 lines
            WebkitBoxOrient: "vertical",
            lineHeight: "1.5rem",
            minHeight: "3rem",
            cursor: 'pointer'
          }}
          onClick={()=>setOpenDetail(true)}
        >
          {record.ProductName}
        </Typography>

        {/* Subtitle */}
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
          {record.StyleNo}
        </Typography>

        {/* Price Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body1" sx={{ color: 'red', fontWeight: 'bold' }}>
            {record.saleprice?record.saleprice.toLocaleString('en-US'):0}₫
          </Typography>
          <Typography
            variant="body2"
            sx={{ textDecoration: 'line-through', color: '#888' }}
          >
            {record.price?record.price.toLocaleString('en-US'):0}₫
          </Typography>
        </Box>

        <Box sx={{ position: "relative", display: "flex", alignItems: "center", mt: 1 }}>
          {showLeft && (
            <IconButton
              onClick={() => scrollByAmount(-100)}
              sx={{
                position: "absolute",
                left: -15, top: -1,
                zIndex: 1,
                bgcolor: "white",
                boxShadow: 1,
                height: '32px !important',
                width: 20,
                borderRadius: "0 20px 20px 0",
                p: 0, pr: 0.5
              }}
            >
              <ChevronLeft sx={{ color: '#36bbff' }} />
            </IconButton>
          )}

          <Box
            ref={scrollRef}
            sx={{
              display: "flex",
              overflowX: "auto",
              gap: 1,
              //pt: 1, pb: 1,
              scrollbarWidth: "none", // Firefox
              "&::-webkit-scrollbar": { display: "none" }, // Chrome/Safari
            }}
          >
            {record.arrColor && record.arrColor.map((label, idx) => (
              <Chip
                key={idx}
                label={label}
                variant="outlined"
                sx={{
                  minWidth: 50,
                  textAlign: "center",
                  bgcolor: label==record.Color ? "cyan" : "transparent",
                  cursor: 'pointer',
                }}
                onClick={()=>handleChangeColor(label, idx)}
              />
            ))}
          </Box>

          {showRight && (
            <IconButton size="small"
              onClick={() => scrollByAmount(100)}
              sx={{
                position: "absolute",
                right: -15, top: -1,
                zIndex: 1,
                bgcolor: "white",
                boxShadow: 1,
                height: '32px !important',
                width: 20,
                borderRadius: "20px 0 0 20px",
                p: 0, pl: 0.5
              }}
            >
              <ChevronRight sx={{ color: '#36bbff' }} />
            </IconButton >
          )}
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<ShoppingCart />}
            sx={{ borderRadius: 2, px: 2, width: '100%' }}
            onClick={()=>setOpenDetail(true)}
          >
            Thêm Vào Giỏ Hàng
          </Button>
        </Box>
      </CardContent>

      <Dialog onClose={()=>setOpenDetail(false)} open={openDetail} maxWidth="xl" fullWidth 
        sx={{ "& .MuiDialog-paperFullWidth": {m: {xs: 1, md: 4}, width: '100%', maxHeight: {xs: '98vh', md: '90vh'}} }}>
        <CardDetail record={record} setOpenDetail={setOpenDetail} />
      </Dialog>
    </Card>
  );
};

export default ShopingCard;
